import { Injectable } from '@angular/core'
import { BehaviorSubject, from, Observable, Subscriber } from 'rxjs'
import { filter } from 'rxjs/operators'
import Centrifuge, { SubscriptionEvents } from 'centrifuge'

import { JwtService } from '@app/modules/auth/jwt.service'
import { ApiService } from '@shared/services/api.service'
import {
  Chat,
  ChatMessage,
  ChatSocketEvent,
  ChatSocketResponse,
  ChatUser,
  StateCentrifuge,
  StateCentrifugeStateEnum,
  StateCentrifugeTypeEnum,
  UserChannelsResponse,
} from '@shared/models/messenger/messenger.model'

@Injectable({
  providedIn: 'root',
})
export class ChatSocketService {
  centrifuge: Centrifuge

  private stateCentrifuge = new BehaviorSubject<StateCentrifuge>(null)
  readonly stateCentrifuge$ = this.stateCentrifuge.asObservable().pipe(filter(Boolean), filter(ChatSocketService.isCentrifugeConnected))

  private privateChannel: string
  private teamChannel: string
  private readonly baseUrlChat: string

  private privateChannelObserver: Observable<ChatSocketResponse>
  private teamChannelObserver: Observable<ChatSocketResponse>

  private activeChannelObserver: Observable<ChatSocketResponse>
  private activeChannelObserverSubscribe: Centrifuge.Subscription

  constructor(
    private readonly apiService: ApiService,
    private readonly jwtService: JwtService,
  ) {
    this.baseUrlChat = apiService.baseUrlChat
  }

  connect(channels: UserChannelsResponse): Centrifuge | null {
    this.teamChannel = channels.team
    this.privateChannel = channels.private

    if (!this.centrifuge) {
      const token = this.jwtService.getToken()
      this.centrifuge = new Centrifuge(`${this.baseUrlChat}:8000/connection/websocket`)
      this.centrifuge.setToken(token)
      this.subscribeCentrifugeEvents()
      this.centrifuge.connect()
    } else if (!this.centrifuge.isConnected()) {
      this.centrifuge.connect()
    }

    return this.centrifuge?.isConnected() ? this.centrifuge : null
  }

  disconnect(): void {
    this.centrifuge?.disconnect()
    this.cleanupPrivateChannel()
    this.cleanupTeamChannel()
    this.cleanupActiveChannel()
    this.centrifuge = null
  }

  connectPrivateChannel = (): Observable<ChatSocketResponse> => {
    if (!this.privateChannelObserver) {
      const observerSubscriber = this.observerSubscriber(this.privateChannel, 'publish')
      this.privateChannelObserver = new Observable(observerSubscriber)
    }

    return this.privateChannelObserver
  }

  connectTeamChannel = (): Observable<ChatSocketResponse> => {
    if (!this.teamChannelObserver) {
      const observerSubscriber = this.observerSubscriber(this.teamChannel, 'publish')
      this.teamChannelObserver = new Observable(observerSubscriber)
    }

    return this.teamChannelObserver
  }

  connectActiveChannel = (channel: string, reset?: boolean): Observable<ChatSocketResponse> => {
    if (!this.activeChannelObserver || reset) {
      const observerSubscriber = (subscriber: Subscriber<ChatSocketResponse>): void => {
        const handleSocket = (message: ChatSocketResponse): void => {
          subscriber.next(message)
        }

        this.activeChannelObserverSubscribe?.unsubscribe()
        this.activeChannelObserverSubscribe = this.centrifuge?.subscribe(channel, handleSocket)
      }

      this.activeChannelObserver = new Observable(observerSubscriber)
    }

    return this.activeChannelObserver
  }

  sendMessage(channel: string, data: ChatSocketEvent): Observable<Centrifuge.PublishResult> {
    return from(this.centrifuge.publish(channel, data))
  }

  private observerSubscriber = (channel: string, eventName?: keyof SubscriptionEvents) => (subscriber: Subscriber<ChatSocketResponse>) => {
    const eventListener = this.handleSubscribeToSocketResponse(subscriber)
    const centrifugeSubscription = this.centrifuge?.subscribe(channel)

    centrifugeSubscription.addListener(eventName, eventListener)

    return {
      unsubscribe: () => {
        centrifugeSubscription.removeListener(eventName, eventListener)
      },
    }
  }

  private subscribeCentrifugeEvents(): void {
    this.centrifuge.on('connect', (ctx) => {
      //.info('[Centrifuge] Connected:', ctx);
      this.stateCentrifuge.next({
        type: StateCentrifugeTypeEnum.State,
        state: StateCentrifugeStateEnum.Connected,
        data: ctx,
      })
    })

    this.centrifuge.on('disconnect', (ctx) => {
      //console.warn('[Centrifuge] Disconnected:', ctx);

      this.stateCentrifuge.next({
        type: StateCentrifugeTypeEnum.State,
        state: StateCentrifugeStateEnum.Disconnected,
        data: ctx,
      })

      this.cleanupPrivateChannel()
      this.cleanupTeamChannel()
      this.cleanupActiveChannel()

      if (ctx.reason === 'expired' || ctx.reason === 'unauthorized') {
        //console.error('[Centrifuge] Token expired or unauthorized – disconnecting permanently.');
        this.centrifuge.disconnect()
      }
    })

    this.centrifuge.on('error', (err) => {
      //console.error('[Centrifuge] Error:', err);
      this.stateCentrifuge.next({
        type: StateCentrifugeTypeEnum.Error,
        state: StateCentrifugeStateEnum.Disconnected,
        data: err,
      })
    })
  }

  private cleanupPrivateChannel = (): void => {
    this.privateChannelObserver = null
  }

  private cleanupTeamChannel = (): void => {
    this.teamChannelObserver = null
  }

  private cleanupActiveChannel = (): void => {
    this.activeChannelObserver = null
    this.activeChannelObserverSubscribe = null
  }

  private handleSubscribeToSocketResponse =
    (subscriber: Subscriber<ChatSocketResponse>) =>
    (messageResponse: ChatSocketResponse): void => {
      subscriber.next(this.handleToSocketResponse(messageResponse))
    }

  private handleToSocketResponse = (messageResponse: ChatSocketResponse): ChatSocketResponse => {
    const {
      data: { user, data },
    } = messageResponse

    delete user?.data

    return {
      ...messageResponse,
      data: {
        ...messageResponse.data,
        ...(user && { user: new ChatUser(user) }),
        data: {
          ...messageResponse.data.data,
          ...(data?.chat && { chat: new Chat(data?.chat) }),
          ...(data?.message?.user && { message: new ChatMessage(data?.message) }),
        },
      },
    }
  }

  static isCentrifugeConnected({ state }: StateCentrifuge) {
    return state === StateCentrifugeStateEnum.Connected
  }
}
